import { Flex, SearchInput, Text, TextButton } from "@amzn/storm-ui";
import {
    MultiSelect,
    MultiSelectItem,
    MultiSelectItemGroup,
} from "@amzn/storm-ui-multi-select";
import { useToggle } from "@react-hookz/web";
import React, { useCallback, useMemo, useState } from "react";
import { isDeepEqual, uniqueWith } from "remeda";

import {
    useClearDimensionValueOnDependencyChange,
    useGetDimensionDisable,
    useSetDimensionValue,
    useUpdateStatusOnChange,
} from "../../hooks/dimension/dimension.hooks";
import { useDimensionHydration } from "../../store/dimension.hydration";
import { DimensionStatus } from "../../store/dimension.status";
import type { MultiSelectDimensionValue } from "../../store/dimension.value";
import type {
    DimensionConfiguration,
    DimensionTemplate,
} from "../../types/dimensions";
import { DimensionTypes } from "../../types/dimensions";
import { buildRequestParameter } from "../../utils/app.utils";
import { LabelEndComponent } from "../common/LabelEndComponent";

import {
    BulkActionsContainer,
    SearchBarContainer,
} from "./MultiSelectDimension.styles";
import { BasicDimensionBox } from "./common/components";

export interface MultiSelectStaticDimensionItem {
    value: string;
    label: string;
    subText?: string;
}

export interface MultiSelectStaticDimensionConfiguration
    extends DimensionConfiguration {
    items: MultiSelectStaticDimensionItem[];
    delimiter?: string;
    enableBulkActions?: boolean;
    maxItemsSelected?: number;
}

export interface MultiSelectStaticDimensionProps extends DimensionTemplate {
    configuration: MultiSelectStaticDimensionConfiguration;
}

export const MultiSelectStaticDimension: React.FunctionComponent<
    MultiSelectStaticDimensionProps
> = (props) => {
    const { id, dependencies, configuration, optional, requestParameterMap } =
        props;
    const {
        label,
        tooltip,
        items,
        delimiter,
        enableBulkActions = true,
        maxItemsSelected,
    } = configuration;

    const disabled = useGetDimensionDisable(dependencies);

    const [
        { selectedItems = [], selectedItemsObjects = [] },
        setDimensionValue,
    ] = useSetDimensionValue<MultiSelectDimensionValue>(
        id,
        buildRequestParameter(
            requestParameterMap ?? {
                value: `${id}_id`,
                selectedItemsObjects: `${id}_selectedObjects`,
            },
        ),
    );

    const [showOnlySelected, toggleShowOnlySelected] = useToggle(false);
    const [search, setSearch] = useState("");

    useClearDimensionValueOnDependencyChange(id, dependencies);

    useUpdateStatusOnChange(id, (data: MultiSelectDimensionValue) => {
        if (optional) {
            return DimensionStatus.SUCCESS;
        }
        if (data.selectedItems && data.selectedItems.length > 0) {
            return DimensionStatus.SUCCESS;
        }
        return DimensionStatus.EMPTY;
    });

    const displayedItems = useMemo(() => {
        const filtered: MultiSelectStaticDimensionItem[] = showOnlySelected
            ? selectedItemsObjects.filter((i) =>
                  i.label.toLowerCase().includes(search.toLowerCase()),
              )
            : items.filter((i) =>
                  i.label.toLowerCase().includes(search.toLowerCase()),
              );

        return filtered;
    }, [showOnlySelected, search, items, selectedItemsObjects]);

    const bulkActionHandler = useCallback(
        (actionValue: string) => {
            if (actionValue === "add") {
                const newSelectedItems = uniqueWith(
                    [...selectedItems, ...displayedItems.map((i) => i.value)],
                    isDeepEqual,
                );
                const newSelectedItemsObjects = uniqueWith(
                    [...selectedItemsObjects, ...displayedItems],
                    isDeepEqual,
                );
                setDimensionValue({
                    type: DimensionTypes.MULTI_SELECT_STATIC,
                    value: delimiter
                        ? newSelectedItems.join(delimiter)
                        : JSON.stringify(newSelectedItems),
                    selectedItems: newSelectedItems,
                    selectedItemsObjects: newSelectedItemsObjects,
                });
            } else if (actionValue === "clear") {
                setDimensionValue({
                    type: DimensionTypes.MULTI_SELECT_STATIC,
                    value: "",
                    selectedItems: [],
                    selectedItemsObjects: [],
                });
            } else if (actionValue === "toggleSelectedVisible") {
                toggleShowOnlySelected();
            }
        },
        [
            setDimensionValue,
            selectedItems,
            selectedItemsObjects,
            displayedItems,
            delimiter,
            toggleShowOnlySelected,
        ],
    );

    const labelEndProps = { tooltip, optional };
    const renderLabelEnd = <LabelEndComponent {...labelEndProps} />;

    const multiSelectItems = useMemo(() => {
        const set = new Set(selectedItems);
        return displayedItems.map((i, index) => (
            <MultiSelectItem
                name={i.label}
                value={i.value}
                key={index}
                disabled={
                    maxItemsSelected !== undefined &&
                    set.size >= maxItemsSelected &&
                    !set.has(i.value)
                }
            >
                <Text>{i.label}</Text>
                {i.subText && (
                    <Text style={{ color: "grey" }} size="xsmall">
                        {i.subText}
                    </Text>
                )}
            </MultiSelectItem>
        ));
    }, [displayedItems, selectedItems, maxItemsSelected]);

    useDimensionHydration(id, dependencies, async (hydrationPayload) => {
        const payloadValues = {
            valueIdLookup: (() => {
                if (requestParameterMap) {
                    return requestParameterMap.value;
                } else {
                    return `${id}_id`;
                }
            })(),
        };

        if (!hydrationPayload[payloadValues.valueIdLookup]) {
            if (optional) {
                return;
            }
            throw new Error("Value not available in payload");
        }

        let selectedValues: string[] = [];
        try {
            if (delimiter) {
                selectedValues =
                    hydrationPayload[payloadValues.valueIdLookup].split(
                        delimiter,
                    );
            } else {
                selectedValues = JSON.parse(
                    hydrationPayload[payloadValues.valueIdLookup],
                );
            }
        } catch (err) {
            throw new Error("Payload value failed to parse");
        }

        if (selectedValues.length > 0) {
            const foundItems = items.filter((item) =>
                selectedValues.includes(item.value),
            );

            if (foundItems.length > 0) {
                const v = foundItems.map((e) => e.value);
                setDimensionValue({
                    type: DimensionTypes.MULTI_SELECT_STATIC,
                    value: delimiter ? v.join(delimiter) : JSON.stringify(v),
                    selectedItems: v as string[],
                    selectedItemsObjects: foundItems,
                });
            } else {
                throw new Error("No matching items found");
            }

            if (foundItems.length !== selectedValues.length) {
                throw new Error("Some items not found");
            }
        }
    });

    return (
        <BasicDimensionBox>
            <MultiSelect
                label={label}
                data-testid={`${id}-multiselect`}
                name={`multi-select-${id}`}
                disabled={disabled}
                fullWidth
                onChange={(v) => {
                    const allSelectedItems = items.filter((i) =>
                        v.includes(i.value),
                    );
                    setDimensionValue({
                        type: DimensionTypes.MULTI_SELECT_STATIC,
                        value: delimiter
                            ? v.join(delimiter)
                            : JSON.stringify(v),
                        selectedItems: v as string[],
                        selectedItemsObjects: allSelectedItems,
                    });
                }}
                onOverrideLabel={(_, selectedValues): string => {
                    if (selectedValues.length === 0) {
                        return "Select Items";
                    }
                    return selectedValues.map((i) => i).join(", ");
                }}
                renderLabelEnd={renderLabelEnd}
                selectedValues={selectedItems}
                secondaryViewCloseButtonLabel="Done"
                withSearchPanel={
                    <SearchBarContainer>
                        <SearchInput
                            id={`search-${id}`}
                            placeholder="Search"
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setSearch(e.target.value);
                            }}
                            onClear={() => setSearch("")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                                if (e.key === " ") {
                                    e.stopPropagation();
                                }
                            }}
                        />
                    </SearchBarContainer>
                }
                withBulkActionPanel={
                    enableBulkActions ? (
                        <BulkActionsContainer>
                            <Flex gap="small">
                                <TextButton
                                    onClick={() => bulkActionHandler("add")}
                                >
                                    Add all
                                </TextButton>
                                <TextButton
                                    onClick={() => bulkActionHandler("clear")}
                                >
                                    Clear all
                                </TextButton>
                                <TextButton
                                    onClick={() =>
                                        bulkActionHandler(
                                            "toggleSelectedVisible",
                                        )
                                    }
                                >
                                    Show{" "}
                                    {showOnlySelected ? "all" : "only selected"}
                                </TextButton>
                                <Text color="tertiary" inline>
                                    {`${selectedItems.length} selected / ${items.length} items`}
                                </Text>
                            </Flex>
                        </BulkActionsContainer>
                    ) : undefined
                }
            >
                {displayedItems.length === 0 ? (
                    <Flex justifyContent="center" padding="medium">
                        <Text>No items available</Text>
                    </Flex>
                ) : (
                    <MultiSelectItemGroup label="">
                        {multiSelectItems}
                    </MultiSelectItemGroup>
                )}
            </MultiSelect>
        </BasicDimensionBox>
    );
};
